import { VideoPreloader } from "../interfaces/VideoPreloader";
import { asArray, sourcesEqual } from "../utils/helper";
import { VideoPreloaderWorker } from "../interfaces/PreloaderWorker";
import { obtainInstanceLogger } from "../logger";
import { StreamData } from "../interfaces/StreamData";
import { QualityLevel } from "../../../model/qualityLevel/QualityLevel";
import { ILogger } from "js-logger";
import { VideoJsPreloaderWorker } from "../workers/VideoJsPreloaderWorker";

export class SingleQualityVideoJsPreloader implements VideoPreloader {
  protected logger: ILogger;
  protected readonly queued = new Array<StreamData>();
  protected readonly finished = new Array<StreamData>();
  protected readonly runningWorkers = new Map<string, VideoPreloaderWorker>();
  private isPaused: boolean = true;

  constructor(
    readonly quality: QualityLevel,
    readonly maxSimultaneousConnections: number = 4,
  ) {
    this.logger = obtainInstanceLogger("SingleQualityVideoJsPreloader(" + quality.dimension + ")");
  }

  queue(sources: StreamData | StreamData[]) {
    for (const source of asArray(sources)) {
      if (this.checkIfAlreadyQueued(source)) {
        continue;
      }

      this.queued.push(source);
    }

    this.logger.log("Adding " + asArray(sources).length + " sources to queue");

    this.doUpdate();
  }

  start() {
    this.logger.log("Starting preloader");

    this.isPaused = false;
    this.doUpdate();
  }

  pause() {
    this.logger.log("Pausing preloader");

    this.isPaused = true;
    this.doUpdate();
  }

  cancelAll() {
    this.eachPreloader((preloader) => {
      preloader.pause();
      preloader.dispose();
    });

    this.runningWorkers.clear();
    this.queued.splice(0, this.queued.length);
  }

  cancel(urls: StreamData | StreamData[]) {
    for (const source of asArray(urls)) {
      const worker = this.runningWorkers.get(source.src);

      if (worker) {
        worker.pause();
        worker.dispose();
      }

      const queuedIndex = this.queued.findIndex((queuedSource) => sourcesEqual(queuedSource, source));

      if (queuedIndex !== -1) {
        this.queued.splice(queuedIndex, 1);
      }
    }

    this.doUpdate();
  }

  protected createWorker(source: StreamData): VideoPreloaderWorker {
    return new VideoJsPreloaderWorker(source, this.quality);
  }

  private checkIfAlreadyQueued(source: StreamData) {
    return new Set(this.getAllSources()).has(source);
  }

  private getAllSources(): StreamData[] {
    const workerSources = Array.from(this.runningWorkers.values()).map((worker) => worker.streamData);

    return ([] as StreamData[]).concat(this.queued).concat(this.finished).concat(workerSources);
  }

  private eachPreloader(callback: (preloader: VideoPreloaderWorker) => void) {
    for (const task of Array.from(this.runningWorkers.values())) {
      callback(task);
    }
  }

  private doUpdate() {
    this.createTasks();
    this.startOrPauseTasks();
  }

  private createTasks() {
    while (this.runningWorkers.size < this.maxSimultaneousConnections) {
      if (this.queued.length === 0) {
        break;
      }

      const source = this.queued.pop()!;
      const worker = this.createWorker(source);

      worker.addObserver({
        onFinish: this.onWorkerFinished.bind(this),
      });

      this.runningWorkers.set(source.src, worker);

      this.logger.log("New worker created (" + source.src + ")");
    }
  }

  private startOrPauseTasks() {
    for (const task of Array.from(this.runningWorkers.values())) {
      try {
        if (this.isPaused && task.isRunning) {
          this.logger.log("Pausing worker", task);
          task.pause();
        }

        if (!this.isPaused && !task.isRunning) {
          this.logger.log("Starting worker", task);
          task.start();
        }
      } catch (e) {
        this.logger.error("Error while starting or pausing worker", task);
        this.logger.error(e);

        this.removeTask(task.streamData);
        this.doUpdate();
      }
    }
  }

  private onWorkerFinished(workerSource: StreamData) {
    this.logger.log("Worker finished successfully (" + workerSource.src + ")");

    this.finished.push(workerSource);
    this.removeTask(workerSource);

    this.doUpdate();
  }

  private removeTask(source: StreamData) {
    const task = this.runningWorkers.get(source.src);

    if (task) {
      task.dispose();
      this.runningWorkers.delete(source.src);
    }
  }
}
